import { Document, Page, Text, View, Image, StyleSheet, pdf } from '@react-pdf/renderer'
import dayjs from 'dayjs'
import { formatCrewRole } from '@/lib/utils'
import {
  PronteerLogo,
  fetchAsDataUrl,
  deliverPdf,
  sanitizeFilename,
} from '@/lib/pdf/shared'

type ItemType = 'YesNo' | 'Text' | 'TextArea' | 'Number'

interface TaskPdfItem {
  id: string
  label: string
  type: ItemType
  isRequired: boolean
  booleanResponse: boolean | null
  textResponse: string | null
  numberResponse: number | null
  note: string | null
  photoS3Keys: string[]
}

interface TaskPdfCrew {
  id: string
  firstName: string
  lastName: string
  name: string
  crewRole: string | null
}

interface TaskPdfTripLeg {
  departureAirportIcao: string
  arrivalAirportIcao: string
  departureDateTime: Date | string
  arrivalDateTime: Date | string
  aircraft: { registration: string }
  trip: { tripName: string }
  crewAssignments: { crew: TaskPdfCrew }[]
}

interface TaskPdfPayload {
  name: string
  status: 'Draft' | 'Submitted' | 'Read'
  items: TaskPdfItem[]
  photoS3Keys: string[]
  submittedBy?: { firstName: string; lastName: string } | null
  submittedAt?: Date | string | null
  tripLeg?: TaskPdfTripLeg | null
}

const styles = StyleSheet.create({
  page: {
    padding: 32,
    fontSize: 11,
    fontFamily: 'Helvetica',
    color: '#0a0a0a',
  },
  header: {
    marginBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e5e5',
    paddingBottom: 12,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
  },
  headerText: {
    flexGrow: 1,
    flexShrink: 1,
  },
  headerLogo: {
    flexShrink: 0,
  },
  title: {
    fontSize: 20,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 4,
  },
  meta: {
    fontSize: 10,
    color: '#737373',
    marginTop: 2,
  },
  statusPill: {
    alignSelf: 'flex-start',
    fontSize: 9,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 9999,
    marginTop: 6,
  },
  sectionHeading: {
    fontSize: 13,
    fontFamily: 'Helvetica-Bold',
    marginTop: 16,
    marginBottom: 8,
  },
  item: {
    marginBottom: 12,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f5f5f5',
  },
  itemLabel: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 11,
    marginBottom: 4,
  },
  itemValue: {
    fontSize: 11,
    color: '#0a0a0a',
  },
  itemEmpty: {
    fontSize: 11,
    color: '#a3a3a3',
    fontStyle: 'italic',
  },
  itemNote: {
    fontSize: 10,
    color: '#737373',
    marginTop: 4,
  },
  photoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  photo: {
    width: 160,
    height: 160,
    objectFit: 'cover',
    borderRadius: 4,
    marginRight: 8,
    marginBottom: 8,
  },
  tripBlock: {
    borderWidth: 1,
    borderColor: '#f5f5f5',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
  },
  tripRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  tripPrimary: {
    fontSize: 13,
    fontFamily: 'Helvetica-Bold',
  },
  tripSecondary: {
    fontSize: 11,
    color: '#0a0a0a',
  },
  tripMuted: {
    fontSize: 10,
    color: '#737373',
  },
  crewBlock: {
    marginTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#f5f5f5',
    paddingTop: 8,
  },
  crewHeading: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    color: '#737373',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 4,
  },
  crewRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: 11,
    marginBottom: 2,
  },
})

function statusColors(status: TaskPdfPayload['status']): { bg: string; fg: string; label: string } {
  if (status === 'Submitted') return { bg: '#dcfce7', fg: '#15803d', label: 'Submitted' }
  return { bg: '#f5f5f5', fg: '#525252', label: 'Draft' }
}

function formatPerson(
  person: { firstName: string; lastName: string } | null | undefined,
  at: Date | string | null | undefined,
): string {
  if (!person) return '—'
  const name = `${person.firstName} ${person.lastName}`
  if (!at) return name
  return `${name} on ${dayjs(at).format('MMM D, YYYY [at] h:mm A')}`
}

function formatResponse(item: TaskPdfItem): string | null {
  if (item.type === 'YesNo') {
    if (item.booleanResponse === true) return 'Yes'
    if (item.booleanResponse === false) return 'No'
    return null
  }
  if (item.type === 'Number') {
    return item.numberResponse !== null ? String(item.numberResponse) : null
  }
  return item.textResponse && item.textResponse.length > 0 ? item.textResponse : null
}

function TaskPdfDocument({
  task,
  photoUrlByKey,
}: {
  task: TaskPdfPayload
  photoUrlByKey: Map<string, string>
}) {
  const pill = statusColors(task.status)
  const responded = task.items.filter((i) => formatResponse(i) !== null).length
  const resolveKeys = (keys: string[]) =>
    keys.map((k) => photoUrlByKey.get(k)).filter((u): u is string => !!u)
  const taskPhotoUrls = resolveKeys(task.photoS3Keys)

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.headerRow}>
            <View style={styles.headerText}>
              <Text style={styles.title}>{task.name}</Text>
              <View style={{ ...styles.statusPill, backgroundColor: pill.bg }}>
                <Text style={{ color: pill.fg }}>{pill.label}</Text>
              </View>
              <Text style={styles.meta}>
                {responded} of {task.items.length} items completed
              </Text>
              <Text style={styles.meta}>
                Submitted by: {formatPerson(task.submittedBy, task.submittedAt)}
              </Text>
            </View>
            <View style={styles.headerLogo}>
              <PronteerLogo width={96} />
            </View>
          </View>
        </View>

        {task.tripLeg && (
          <View style={styles.tripBlock} wrap={false}>
            <View style={styles.tripRow}>
              <Text style={styles.tripPrimary}>{task.tripLeg.trip.tripName}</Text>
              <Text style={styles.tripMuted}>
                {task.tripLeg.aircraft.registration}
              </Text>
            </View>
            <View style={styles.tripRow}>
              <Text style={styles.tripSecondary}>
                {task.tripLeg.departureAirportIcao} → {task.tripLeg.arrivalAirportIcao}
              </Text>
              <Text style={styles.tripSecondary}>
                {dayjs(task.tripLeg.departureDateTime).format('MMM D, YYYY')}
              </Text>
            </View>
            <View style={styles.crewBlock}>
              <Text style={styles.crewHeading}>Crew</Text>
              {task.tripLeg.crewAssignments.length === 0 ? (
                <Text style={styles.tripMuted}>No crew assigned</Text>
              ) : (
                task.tripLeg.crewAssignments.map(({ crew: c }) => (
                  <View key={c.id} style={styles.crewRow}>
                    <Text>
                      {c.firstName && c.lastName
                        ? `${c.firstName} ${c.lastName}`
                        : c.name}
                    </Text>
                    <Text style={styles.tripMuted}>{formatCrewRole(c.crewRole)}</Text>
                  </View>
                ))
              )}
            </View>
          </View>
        )}

        <Text style={styles.sectionHeading}>Checklist</Text>
        {task.items.length === 0 && (
          <Text style={styles.itemEmpty}>This task has no items.</Text>
        )}
        {task.items.map((item) => {
          const value = formatResponse(item)
          const itemPhotoUrls = resolveKeys(item.photoS3Keys)
          return (
            <View key={item.id} style={styles.item} wrap={false}>
              <Text style={styles.itemLabel}>
                {item.label}
                {item.isRequired ? ' *' : ''}
              </Text>
              {value !== null ? (
                <Text style={styles.itemValue}>{value}</Text>
              ) : (
                <Text style={styles.itemEmpty}>No answer</Text>
              )}
              {item.note && item.note.trim().length > 0 && (
                <Text style={styles.itemNote}>Note: {item.note}</Text>
              )}
              {itemPhotoUrls.length > 0 && (
                <View style={styles.photoGrid}>
                  {itemPhotoUrls.map((src, i) => (
                    <Image key={i} src={src} style={styles.photo} />
                  ))}
                </View>
              )}
            </View>
          )
        })}

        {taskPhotoUrls.length > 0 && (
          <>
            <Text style={styles.sectionHeading}>Photos</Text>
            <View style={styles.photoGrid}>
              {taskPhotoUrls.map((src, i) => (
                <Image key={i} src={src} style={styles.photo} />
              ))}
            </View>
          </>
        )}
      </Page>
    </Document>
  )
}

export interface ExportTaskPdfParams {
  task: TaskPdfPayload
  getDownloadUrl: (s3Key: string) => Promise<string>
}

/**
 * Generate a PDF for the task and hand it off: native share sheet on iOS,
 * file download on web.
 */
export async function exportTaskAsPdf({ task, getDownloadUrl }: ExportTaskPdfParams): Promise<void> {
  const allKeys = Array.from(
    new Set([...task.photoS3Keys, ...task.items.flatMap((i) => i.photoS3Keys)]),
  )
  const photoUrlByKey = new Map<string, string>()
  await Promise.all(
    allKeys.map(async (key) => {
      const url = await getDownloadUrl(key).catch(() => null)
      if (!url) return
      const dataUrl = await fetchAsDataUrl(url)
      if (dataUrl) photoUrlByKey.set(key, dataUrl)
    }),
  )

  const blob = await pdf(
    <TaskPdfDocument task={task} photoUrlByKey={photoUrlByKey} />,
  ).toBlob()

  await deliverPdf(blob, buildTaskPdfFilename(task), task.name)
}

/**
 * Build an archive-friendly filename, e.g.
 * `preflight-checklist_trip-042_n123ab_ltba-egll_2026-09-02.pdf`.
 * Falls back to just the checklist name when the task isn't tied to a flight.
 */
export function buildTaskPdfFilename(task: TaskPdfPayload): string {
  const leg = task.tripLeg
  const parts = [task.name]
  if (leg) {
    parts.push(
      leg.trip.tripName,
      leg.aircraft.registration,
      `${leg.departureAirportIcao}-${leg.arrivalAirportIcao}`,
      dayjs(leg.departureDateTime).format('YYYY-MM-DD'),
    )
  }
  const name = parts
    .map((part) => sanitizeFilename(part.trim(), ''))
    .filter(Boolean)
    .join('_')
  return `${name || 'task'}.pdf`
}
